import React, { useEffect, useRef, useState, memo } from 'react';
import { useReactFlow } from 'reactflow';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GlobeMinimapProps, NodeObject } from '../../utilities/types/common';
import { useLayerContext } from '../../context/workbench/LayerContext';
import { useNodesWithPositionContext } from '../../context/workbench/NodesWithPositionContext';

const GlobeMinimap: React.FC<GlobeMinimapProps> = ({
  width = 200,
  height = 200,
  className = ''
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const { setViewport } = useReactFlow();
  const layerContext = useLayerContext();
  const { nodesWithPosition } = useNodesWithPositionContext()

  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  // New state for hover
  const [hoveredNode, setHoveredNode] = useState<{
    id: string;
    data?: any;
  } | null>(null);

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
    cameraRef.current = camera;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setClearColor(0x000000, 0); // Transparent background
    renderer.setSize(width, height);
    mountRef.current.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.rotateSpeed = 0.5;
    controls.enableZoom = false;
    controls.minDistance = 10;
    controls.maxDistance = 20;
    controlsRef.current = controls;

    const sphereGeometry = new THREE.SphereGeometry(5, 32, 32);

    // Texture with only grid pattern (no gradient)
    const size = 512;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0)'; // Transparent background
      ctx.fillRect(0, 0, size, size);

      // Draw diamond grid
      const gradient = ctx.createLinearGradient(0, 0, size, size);
      gradient.addColorStop(0, '#A789DE'); // Light purple
      gradient.addColorStop(1, '#6A5ACD'); // Darker purple
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 4;
      const gridSize = 32;
      for (let i = 0; i <= size; i += gridSize) {
        for (let j = 0; j <= size; j += gridSize) {
          ctx.beginPath();
          ctx.moveTo(i - gridSize / 2, j);
          ctx.lineTo(i, j + gridSize / 2);
          ctx.lineTo(i + gridSize / 2, j);
          ctx.lineTo(i, j - gridSize / 2);
          ctx.closePath();
          ctx.stroke();
        }
      }
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(4, 2);

    // Base sphere material
    const baseMaterial = new THREE.MeshPhongMaterial({
      color: 0xDDA0DD, // Light purple base
      transparent: true,
      opacity: 0.2,
    });

    // Grid material
    const gridMaterial = new THREE.MeshPhongMaterial({
      map: texture,
      transparent: true,
      opacity: 1,
      color: 0xA789DE,
      shininess: 50,
    });

    const baseSphere = new THREE.Mesh(sphereGeometry, baseMaterial);
    const gridSphere = new THREE.Mesh(sphereGeometry, gridMaterial);
    gridSphere.scale.multiplyScalar(1.001);

    scene.add(baseSphere);
    scene.add(gridSphere);

    // Add static gradient background
    const backgroundGeometry = new THREE.PlaneGeometry(100, 100);
    const backgroundMaterial = new THREE.ShaderMaterial({
      vertexShader: `
      void main() {
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
      fragmentShader: `
      void main() {
        vec2 uv = gl_FragCoord.xy / vec2(512.0, 512.0); // Adjust based on renderer size
        vec3 color1 = vec3(139.0/255.0, 0.0/255.0, 139.0/255.0); // Dark purple
        vec3 color2 = vec3(186.0/255.0, 85.0/255.0, 211.0/255.0); // Lighter purple
        gl_FragColor = vec4(mix(color1, color2, uv.x), 1.0); // Left to right gradient
      }
    `,
      transparent: true,
    });
    const background = new THREE.Mesh(backgroundGeometry, backgroundMaterial);
    background.position.z = -10; // Place behind the globe
    scene.add(background);

    // Lighting setup
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
    scene.add(ambientLight);

    const pointLight = new THREE.PointLight(0xffffff, 1);
    pointLight.position.set(10, 10, 10);
    scene.add(pointLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight.position.set(-10, 10, 10);
    scene.add(directionalLight);

    camera.position.z = 15;

    // Raycaster for node selection
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const handleGetingNodeObject = (data: any) => {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((data.clientX - rect.left) / width) * 2 - 1;
      mouse.y = -((data.clientY - rect.top) / height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(scene.children, true);

      for (const intersect of intersects) {
        const object = intersect.object as NodeObject;
        if (object.nodeId) {
          return object;
        } else {
          return null;
        }

      }
    }

    // Mouse move handler for hover
    const onMouseMove = (event: MouseEvent) => {

      const nodeObject = handleGetingNodeObject(event);
      let foundNode = null;
      if (nodeObject?.nodeId) {
        const node = nodesWithPosition.find((n: any) => n.id === nodeObject.nodeId);
        if (node) {
          foundNode = {
            id: nodeObject.nodeId,
            data: node,
          }
        } else {
          foundNode = null;
        }
      }
      setHoveredNode(foundNode);
    };

    // Handle click events
    const onMouseClick = (event: MouseEvent) => {

      const nodeObject = handleGetingNodeObject(event);
      if (nodeObject?.nodeId) {
        setSelectedNode(nodeObject.nodeId);
        const nodes = nodesWithPosition;
        const node = nodes.find((n: any) => n.id === nodeObject.nodeId);
        if (node) {
          layerContext.setLayerSelectedNodeId(nodeObject.nodeId);
        }
      }
    };

    renderer.domElement.addEventListener('click', onMouseClick);
    renderer.domElement.addEventListener('mousemove', onMouseMove);

    // Update node positions
    const updateNodes = () => {
      // Remove existing nodes but keep the spheres and lights
      scene.children = scene.children.filter(
        child => child === baseSphere || child === gridSphere || child instanceof THREE.Light
      );
      if (nodesWithPosition && nodesWithPosition.length > 0) {
        const nodes = nodesWithPosition;
        const pointGeometry = new THREE.SphereGeometry(0.2, 16, 16);
        const glowGeometry = new THREE.SphereGeometry(0.3, 16, 16);

        // Filter out unwanted node types
        const filteredNodes = nodes.filter((node: any) => {
          const excludedTypes = ['addButtonNode', 'uploadButtonNode', 'choiceNode', 'qaNode'];
          return !excludedTypes.includes(node.type);
        });

        filteredNodes.forEach((node: any) => {
          const phi = (node?.position?.x / width) * Math.PI * 2;
          const theta = (node?.position?.y / height) * Math.PI;

          const isSelected = node.id === selectedNode;

          const pointMaterial = new THREE.MeshPhongMaterial({
            color: isSelected ? 0x1F51FF : 0xffffff,
            emissive: isSelected ? 0x1F51FF : 0x000000,
            emissiveIntensity: isSelected ? 0.5 : 0
          });

          const point = new THREE.Mesh(pointGeometry, pointMaterial) as NodeObject;
          point.nodeId = node.id;

          point?.position.setFromSpherical(
            new THREE.Spherical(5, theta, phi)
          );

          if (isSelected) {
            const glowMaterial = new THREE.MeshBasicMaterial({
              color: 0xa0c4ff,
              transparent: true,
              opacity: 0.5
            });

            const glow = new THREE.Mesh(glowGeometry, glowMaterial);
            glow.position.copy(point?.position);
            scene.add(glow);
          }

          scene.add(point);
        });
      }
    };

    // Reset function
    const resetToDefault = () => {
      camera.position.set(0, 0, 15);
      controls.target.set(0, 0, 0);
      controls.update();
      setViewport({ x: 600, y: 0, zoom: 0.5 }, { duration: 800 });
      setSelectedNode(null);
    };

    // Add "set to origin" text with click handler
    const createOriginText = () => {
      const div = document.createElement('div');
      div.style.position = 'absolute';
      div.style.bottom = '20px'; // Adjusted: increased bottom margin
      div.style.left = '50%';
      div.style.transform = 'translateX(-50%)';
      div.style.color = 'white';
      div.style.fontSize = '12px';
      div.style.fontWeight = "400";
      div.style.display = 'flex';
      div.style.alignItems = 'center';
      div.style.gap = '5px';
      div.style.cursor = 'pointer';
      div.style.zIndex = '6';

      const dot = document.createElement('span');
      dot.style.width = '8px';
      dot.style.height = '8px';
      dot.style.backgroundColor = '#3b82f6';
      dot.style.borderRadius = '50%';
      dot.style.boxShadow = '0px 0px 4px 0px rgba(160, 196, 255, 1)';
      dot.style.display = 'inline-block';

      div.appendChild(dot);
      div.appendChild(document.createTextNode('set to origin'));

      div.addEventListener('click', resetToDefault);
      mountRef.current?.appendChild(div);
      return div;
    };

    const originText = createOriginText();

    // Animation loop
    let frameId: number;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };

    // Initial setup
    updateNodes();
    animate();

    // Cleanup
    return () => {
      cancelAnimationFrame(frameId);
      renderer.domElement.removeEventListener('click', onMouseClick);
      renderer.domElement.removeEventListener('mousemove', onMouseMove);
      originText.removeEventListener('click', resetToDefault);
      originText.remove();
      renderer.dispose();
      if (mountRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, [width, height, setViewport, selectedNode, nodesWithPosition, layerContext]);

  // Calculate container height - increased to make space for text
  // const containerHeight = height + 40; // Add 40px extra space below the globe

  return (
    <div
      ref={mountRef}
      className={`absolute bottom-5 right-5 rounded-lg overflow-hidden shadow-lg ${className}`}
      style={{
        width: `350px`,
        minHeight: `240px`,
        maxHeight: `fit-content`,
        height: `auto`,
        position: 'absolute',
        backgroundColor: '#00000040',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        display: `flex`,
        justifyContent: `center`,
        alignItems: `flex-start`, // Changed to align-items: flex-start
      }}
    >
      {hoveredNode && (
        <div
          style={{
            position: 'absolute',
            top: "10px",
            left: "10px",
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            maxWidth: '200px',
            color: 'black',
            fontSize: '12px',
          }}
        >
          <strong>Type:</strong> {hoveredNode.data?.type || 'Unknown'}<br />
          {hoveredNode.data?.data?.label && (
            <>
              <strong>Label:</strong> {hoveredNode.data.data.label}
            </>
          )}<br />
          <strong>Content:</strong> {hoveredNode.data?.data?.content || "No Content Provided"}<br />
        </div>
      )}

    </div>
  );
};

export default memo(GlobeMinimap);